import { useFormState } from 'informed';
import { getResizedImage } from 'shared-helpers/common';
import { localeNames } from 'shared-locales';
import { useDateNow, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import {
  CertificatesPreview,
  CertificatesSelectTemplate
} from 'components/admin/settings/certificates';
import { CourseCompletionCustomContent } from 'components/courses/common';
import {
  Checkbox,
  Emails,
  FormGroup,
  FormSection,
  Select,
  TextArea
} from 'components/common/form';
import { FileUploader } from 'components/common/uploader';

import type { CourseCompletionSettings } from './SettingsCompletion';

const SettingsCompletionCertificate = ({
  backgroundImageUploaderRef,
  course
}) => {
  const { dateNow } = useDateNow();

  const { messages, serverConfig, user } = useReduxState();

  const formState = useFormState<CourseCompletionSettings>();

  return (
    <FormSection title={messages.settingsCertificate}>
      {serverConfig.certificatesEnableSelection && (
        <CertificatesSelectTemplate
          disabled={!formState.values?.completionEnabled}
          maxWidth={200}
          type="course"
        />
      )}

      {serverConfig.certificatesEnableCourseBackground ? (
        <FormGroup
          htmlFor="certificate-background-image"
          label={messages.settingsCertificateCustomBackgroundImage}
          style={{ marginBottom: 25 }}
        >
          <div style={{ display: 'inline-block' }}>
            <FileUploader
              allowedFileTypes={['image/*']}
              disabled={!formState.values?.completionEnabled}
              getUploader={(ref) => (backgroundImageUploaderRef.current = ref)}
              id="certificate-background-image"
              initialFilename={
                course.certificateBackgroundImage?.split('/').pop() || ''
              }
              keyPrefix="uploads/images/original/"
              uuidAsFilename={false}
            />
          </div>
        </FormGroup>
      ) : null}

      <Checkbox
        disabled={
          !formState.values.completionExpiration ||
          !formState.values.completionEnabled
        }
        field="certificateShowExpirationDate"
        id="certificate-show-expiration-date"
        label={messages.settingsCertificateShowExpirationDate}
      />
      <Checkbox
        disabled={!formState.values.completionEnabled}
        field="certificateShowScore"
        id="certificate-show-score"
        label={messages.settingsCertificateShowScore}
        style={{ marginTop: 10 }}
      />

      <FormGroup
        htmlFor="certificate-custom-content"
        label={messages.settingsCertificateCustomContent}
      >
        <TextArea
          disabled={!formState.values.completionEnabled}
          field="certificateCustomContent"
          id="certificate-custom-content"
          maxLength={180}
        />
      </FormGroup>

      <div style={{ marginTop: 10 }}>
        <CertificatesPreview
          backgroundImage={
            serverConfig.certificatesEnableCourseBackground &&
            course.certificateBackgroundImage
              ? getResizedImage({
                  url: course.certificateBackgroundImage,
                  size: 1920
                })
              : null
          }
          certificateType="course"
          customContent={formState.values.certificateCustomContent || ''}
          disabled={!formState.values.completionEnabled}
          expirationTime={formState.values.completionExpirationValue}
          showExpirationDate={
            formState.values.completionExpiration &&
            formState.values.certificateShowExpirationDate
          }
          showScore={formState.values.certificateShowScore}
          templateId={
            formState.values?.CertificateTemplateId ||
            course.certificateTemplateId ||
            serverConfig.defaultCertificateTemplateId
          }
          title={course.title}
        />
      </div>

      <FormGroup label={messages.commonDownload} style={{ marginTop: 50 }}>
        <Checkbox
          disabled={!formState.values.completionEnabled}
          field="certificateAllowDownloading"
          id="certificate-allow-downloading"
          label={messages.settingsCertificateAllowDownloading}
        />
      </FormGroup>

      <FormGroup label={messages.commonEmail}>
        <Checkbox
          disabled={!formState.values.completionEnabled}
          field="certificateSendToUser"
          id="certificate-send-to-user"
          label={messages.settingsCertificateSendToUser}
        />
        <Checkbox
          disabled={!formState.values.completionEnabled}
          field="certificateSendToOthers"
          id="certificate-send-to-others"
          label={messages.settingsCertificateSendToEmails}
          style={{ marginTop: 10 }}
        />
        <Emails
          disabled={
            !formState.values.completionEnabled ||
            !formState.values.certificateSendToOthers
          }
          field="certificateOtherEmails"
          id="completion-certificate-emails"
          style={{ marginLeft: 20, marginTop: 8 }}
        />
      </FormGroup>

      <FormGroup
        htmlFor="certificate-language"
        label={messages.settingsCertificateLanguage}
      >
        <Select
          disabled={
            !formState.values.completionEnabled ||
            (!formState.values.certificateSendToUser &&
              !formState.values.certificateSendToOthers)
          }
          field="certificateLanguage"
          id="certificate-language"
          options={Object.keys(localeNames).map((localeKey) => ({
            label: localeNames[localeKey],
            value: localeKey
          }))}
          style={{ maxWidth: 200 }}
        />
      </FormGroup>

      {serverConfig.courseCompletionEmailCustomContent ? (
        <FormGroup
          label={messages.commonCustomContent}
          style={{ marginTop: 25 }}
        >
          <Checkbox
            disabled={!formState.values.completionEnabled}
            field="completionEmailShowCustomContent"
            id="completion-email-show-custom-content"
            label={messages.settingsCertificateEmailShowCustomContent}
          />

          <Checkbox
            disabled={!formState.values.completionEnabled}
            field="completionEmailOthersShowCustomContent"
            id="completion-email-others-show-custom-content"
            label={messages.settingsCertificateEmailOthersShowCustomContent}
            style={{ marginTop: 5 }}
          />

          <div
            className="custom-content"
            style={{
              margin: '30px 15px 15px',
              opacity: formState.values.completionEmailShowCustomContent
                ? 1
                : 0.5
            }}
          >
            <CourseCompletionCustomContent
              course={course}
              courseCompletion={{
                completionDate: getFormattedDate({
                  date: dateNow,
                  dateFormat: 'y-MM-dd HH:mm:ss'
                }),
                courseTitle: course.title,
                expirationDate: null,
                tasksPercentage: '79%',
                tasksPoints: '15/19',
                tasksProgress: 0.7,
                readingProgress: 0.9,
                videoProgress: 0.5,
                courseWorkload: course.completionWorkloadValue,
                inactive: false,
                status: 'success',
                isManuallySet: false,
                id: 1
              }}
              showEmailContent={true}
              targetUser={user}
            />
          </div>
        </FormGroup>
      ) : null}
    </FormSection>
  );
};

export default SettingsCompletionCertificate;
